// src/pages/VehicleV2/EquipmentListing.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import {
  FaPlus,
  FaSearch,
  FaList,
  FaTrash,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const LIST_API = "http://nissi-india.com/api/equipmentListing";
const ENTRY_API = "http://nissi-india.com/api/equipment";

const onlyAllowed = (s) => /^[a-zA-Z0-9 ]*$/.test(s || "");
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const EquipmentListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginUserId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchActive =
    String(
      localStorage.getItem("BranchActive") ||
        sessionStorage.getItem("BranchActive") ||
        "1"
    ) === "1";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const [showForm, setShowForm] = useState(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [equipmentId, setEquipmentId] = useState("");
  const [equipmentType, setEquipmentType] = useState("");
  const isEdit = String(equipmentId || "").trim() !== "";

  const normalizeRow = (row, idx = 0) => ({
    ...row,
    equipmentId:
      row?.fld_equipmentid || row?.equipmentId || row?.EquipmentId || row?.id || 0,
    equipmentType:
      row?.fld_equipmenttype ||
      row?.equipmentType ||
      row?.EquipmentType ||
      row?.name ||
      row?.["Vehicle Type"] ||
      "-",
    slno: row?.slno || idx + 1,
  });

  const loadList = async (options = {}) => {
    const nextPage = options.page ?? page;
    const nextLimit = options.limit ?? limit;
    const nextSearch = options.search ?? search;

    setLoading(true);
    setHasSearched(true);

    try {
      const r = await axios.get(`${LIST_API}/list`, {
        params: { search: nextSearch || "", page: nextPage, limit: nextLimit },
        headers: commonHeaders,
      });

      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to load list");
      }

      const list = Array.isArray(r.data?.rows)
        ? r.data.rows
        : Array.isArray(r.data?.data)
        ? r.data.data
        : Array.isArray(r.data?.list)
        ? r.data.list
        : [];

      const normalized = list.map((item, idx) => normalizeRow(item, idx));
      setRows(normalized);
      setTotal(toInt(r.data?.total, normalized.length));
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load equipment list");
      setRows([]);
      setTotal(0);
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEquipmentId("");
    setEquipmentType("");
    sessionStorage.removeItem("EquipID");
  };

  const onSearch = async () => {
    setPage(1);
    await loadList({ page: 1, search });
  };

  const loadViewAll = async () => {
    setSearch("");
    setPage(1);
    await loadList({ page: 1, search: "", limit });
  };

  const clearFilters = async () => {
    setSearch("");
    setPage(1);
    await loadList({ page: 1, search: "" });
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEdit = async (row) => {
    const id = String(row.equipmentId || "");
    if (!id) return;

    setShowForm(true);
    setEquipmentId(id);
    sessionStorage.setItem("EquipID", id);

    try {
      const r = await axios.get(`${ENTRY_API}/view`, {
        params: { equipmentId: id },
        headers: commonHeaders,
      });

      const rowData = r.data?.row || r.data?.data || row;
      setEquipmentType(
        String(
          rowData?.fld_equipmenttype ||
            rowData?.equipmentType ||
            rowData?.EquipmentType ||
            row.equipmentType ||
            ""
        )
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setEquipmentType(String(row.equipmentType || ""));
      toast.error(err?.response?.data?.message || "Failed to load equipment");
    }
  };

  const validate = () => {
    const v = String(equipmentType || "");
    if (v.trim() === "") {
      toast.error("Please enter Equipment Type");
      return false;
    }
    if (v.replace(/\s/g, "").length === 0) {
      toast.error("Please enter Equipment Name");
      return false;
    }
    if (!onlyAllowed(v)) {
      toast.error("Only letters, numbers and spaces are allowed");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!branchActive) {
      toast.error("Submit disabled (Branch not active)");
      return;
    }
    if (!validate()) return;

    try {
      setSaving(true);
      const payload = {
        equipmentId: isEdit ? toInt(equipmentId) : 0,
        equipmentType: equipmentType.trim(),
        loginUserId: toInt(userId),
      };

      const r = await axios.post(`${ENTRY_API}/save`, payload, {
        headers: commonHeaders,
      });

      if (r.data?.success === false) {
        toast.warn(r.data?.message || "Save failed");
        return;
      }

      toast.success(
        r.data?.message || (isEdit ? "Updated Successfully" : "Inserted Successfully")
      );
      resetForm();
      await loadList({ page });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleSelect = (equipId) => {
    const id = String(equipId);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allSelected =
    rows.length > 0 && selectedIds.length === rows.length;

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(rows.map((r) => String(r.equipmentId)));
  };

  const canMovePage = () => {
    if (selectedIds.length > 0) {
      toast.warn(
        "Please delete the selected record(s) in current page and proceed to next page"
      );
      return false;
    }
    return true;
  };

  const gotoPage = (p) => {
    if (!canMovePage()) return;
    setPage(Math.min(Math.max(1, p), totalPages));
  };

  const handleDeleteSelected = async () => {
    if (!branchActive) return;
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record to delete.");
      return;
    }

    const ok = window.confirm(`Are you sure you want to delete ${selectedIds.length} record(s)?`);
    if (!ok) return;

    try {
      const r = await axios.post(
        `${LIST_API}/delete`,
        { ids: selectedIds.map((x) => toInt(x)), loginUserId: toInt(userId) },
        { headers: commonHeaders }
      );

      const deletedCount = toInt(r.data?.deletedCount, 0);
      const msg =
        r.data?.message ||
        (deletedCount === selectedIds.length
          ? "Deleted Successfully"
          : deletedCount > 0
          ? "Equipment(s) in use cannot be removed, others Removed successfully"
          : "Equipment(s) in use cannot be deleted");

      if (deletedCount > 0 && deletedCount < selectedIds.length) toast.warn(msg);
      else if (deletedCount > 0) toast.success(msg);
      else toast.warn(msg);

      const nextTotal = Math.max(0, total - deletedCount);
      const nextPages = Math.max(1, Math.ceil(nextTotal / limit));
      const nextPage = Math.min(page, nextPages);
      setPage(nextPage);
      await loadList({ page: nextPage });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  useEffect(() => {
    loadList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EQUIPMENT LISTING</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Equipment Listing
            </h2>

            <div className="flex items-center gap-3 flex-wrap">
             

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* FORM ABOVE LIST */}
          {showForm && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  {isEdit ? "Edit Equipment" : "Add Equipment"}
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={resetForm}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      <FaTimes /> Clear
                    </span>
                  </button>

                  {branchActive && (
                    <button
                      onClick={handleSubmit}
                      disabled={saving}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                        saving ? "opacity-60 cursor-not-allowed" : ""
                      } ${buttonClass} ${hoverClass}`}
                    >
                      <FaSave />
                      {saving ? "Saving..." : isEdit ? "Update" : "Save"}
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>
                    Equipment Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={equipmentType}
                    maxLength={50}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (onlyAllowed(next)) setEquipmentType(next);
                    }}
                    className={inputClass}
                    placeholder="Enter equipment type..."
                    disabled={saving}
                  />
                </div>
              </div>
            </div>
          )}

          {/* SEARCH */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={clearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>
                  <button
                    onClick={loadViewAll}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    <FaList /> View All
                  </button>
                  <button
                    onClick={onSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Search Equipment</label>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onSearch()}
                    placeholder="Type to search equipment..."
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TABLE */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <>
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleAll}
                          disabled={!branchActive || rows.length === 0}
                          className="w-4 h-4 cursor-pointer"
                          title="Select All"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                    {rows.map((row, idx) => {
                      const id = String(row.equipmentId);
                      return (
                        <tr
                          key={`${id}_${idx}`}
                          className={`${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-white"
                              : isDark
                              ? "bg-gray-750"
                              : "bg-gray-50"
                          } ${
                            String(equipmentId) === id
                              ? isDark
                                ? "ring-1 ring-blue-400"
                                : "bg-blue-50"
                              : ""
                          } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                        >
                          <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * limit + idx + 1}
                          </td>
                          <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                            {row.equipmentType}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              disabled={!branchActive}
                              className={`text-yellow-500 hover:text-yellow-600 text-lg ${
                                !branchActive ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelect(id)}
                              disabled={!branchActive}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {branchActive && (
                  <div className="p-4">
                    <button
                      onClick={handleDeleteSelected}
                      disabled={selectedIds.length === 0}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow text-white ${
                        selectedIds.length === 0
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      <FaTrash />
                      Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mt-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{totalPages}</b> &nbsp; | &nbsp; Total: <b>{total}</b>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  if (!canMovePage()) return;
                  setLimit(toInt(e.target.value, 10));
                  setPage(1);
                }}
                className={inputClass.replace("w-full ", "w-auto ")}
              >
                {[10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <button
                onClick={() => gotoPage(page - 1)}
                disabled={page <= 1}
                className={`px-3 py-2 border rounded-lg ${
                  page <= 1 ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Prev
              </button>
              <button
                onClick={() => gotoPage(page + 1)}
                disabled={page >= totalPages}
                className={`px-3 py-2 border rounded-lg ${
                  page >= totalPages ? "opacity-50 cursor-not-allowed" : ""
                } ${isDark ? "border-gray-600 text-white" : "border-gray-300 text-gray-700"}`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default EquipmentListing;